import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Phone } from "lucide-react";

import { GrannyFlatEnquiryForm } from "@/components/granny-flat-enquiry-form";
import { PageHero } from "@/components/page-parts";
import { FIRM, FIRM_ADDRESS, localBusinessSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Do we both need our own lawyer?",
    a: "Yes. A granny flat arrangement involves competing interests — the older person contributing money and the owner holding the house — so one solicitor cannot act for everyone. We usually act for one side and the other side gets independent advice, which is also what protects the agreement if it is ever challenged.",
  },
  {
    q: "What should we bring to the first appointment?",
    a: "The title details for the property, any council approval for the granny flat, an idea of the contribution and where it is coming from, and both parties' current wills and enduring powers of attorney if they exist. Our granny flat questions checklist covers the rest.",
  },
  {
    q: "Will this affect the age pension?",
    a: "It can. Centrelink has a specific granny flat interest concept and a reasonableness test. A genuine, properly documented arrangement can avoid the five-year gifting rules, while a loose transfer of money often will not. We work through it before anything is paid.",
  },
  {
    q: "How much does the advice cost?",
    a: `The first discussion is obligation free and we quote a fixed fee in writing before any drafting starts. Call ${FIRM.phone} if you would rather talk it through first.`,
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Granny flat arrangement advice and agreements (Queensland)",
  serviceType: "Elder law — granny flat agreements",
  provider: { "@id": "https://coastsidelaw.com.au/#legalservice" },
  areaServed: "Sunshine Coast, Queensland",
};

export const Route = createFileRoute("/granny-flat-consultation")({
  head: () => ({
    meta: [
      { title: fitTitle(`Granny Flat Agreement Lawyers QLD — Consultation | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Request a granny flat consultation with Coastside Law, Golden Beach. Short intake on your role, the older person's age and where the money is coming from — then a fixed-fee quote."),
      },
      { property: "og:title", content: fitTitle(`Granny flat consultation | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Advice on granny flat arrangements in Queensland — ownership, leases, licences, pension and duty. Book a consultation with a Caloundra elder law solicitor."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "/granny-flat-consultation" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/granny-flat-consultation" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localBusinessSchema) },
      { type: "application/ld+json", children: JSON.stringify(serviceSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema) },
    ],
  }),
  component: GrannyFlatConsultationPage,
});

function GrannyFlatConsultationPage() {
  return (
    <>
      <PageHero
        eyebrow="Elder law"
        title="Granny flat consultation"
        lead="Moving in with family and putting money in? Tell us who you are in the arrangement, the age of the older person and where the funds are coming from, and we'll come back with the structure options and a fixed fee."
      />

      <section className="mx-auto grid max-w-6xl gap-12 px-5 py-16 lg:grid-cols-[1fr_1.4fr]">
        <div className="space-y-8 text-sm">
          <div>
            <h2 className="font-serif text-xl text-foreground">What we cover</h2>
            <ul className="mt-4 space-y-3 text-muted-foreground">
              <li>What the older person actually receives — title share, lease, right to reside or a repayable loan.</li>
              <li>How the money comes back on aged care, sale, separation, bankruptcy or death.</li>
              <li>Centrelink granny flat interest and the gifting rules.</li>
              <li>Transfer duty, CGT and updating both wills at the same time.</li>
            </ul>
          </div>

          <div className="rounded-sm border border-border bg-secondary/50 p-5">
            <p className="text-xs font-semibold uppercase tracking-wider text-accent">
              Before you come in
            </p>
            <a
              href="/granny-flat-arrangement-questions-checklist.pdf"
              download
              className="mt-3 inline-flex items-center gap-2 font-serif text-lg text-primary underline"
            >
              <Download className="size-4" aria-hidden="true" />
              Granny flat questions checklist (PDF)
            </a>
            <p className="mt-2 text-muted-foreground">
              Two pages of the questions every family should answer in writing before money changes
              hands.
            </p>
          </div>

          <div>
            <h2 className="font-serif text-xl text-foreground">Where we meet</h2>
            <p className="mt-2 text-muted-foreground">{FIRM_ADDRESS}</p>
            <p className="mt-4 flex items-center gap-2">
              <Phone className="size-4 text-primary" aria-hidden="true" />
              <a href={FIRM.phoneHref} className="font-serif text-2xl text-primary underline">
                {FIRM.phone}
              </a>
            </p>
            <p className="mt-3 text-muted-foreground">
              We also attend clients at home or in aged care around Caloundra when getting out is
              difficult.
            </p>
          </div>

          <p className="text-muted-foreground">
            Not sure which structure suits?{" "}
            <Link
              to="/guides/$slug"
              params={{ slug: "granny-flat-options-compared-ownership-lease-licence" }}
              className="text-primary underline"
            >
              Compare ownership, life interests, leases and licences
            </Link>
            .
          </p>
        </div>

        <div id="granny-flat-enquiry">
          <GrannyFlatEnquiryForm />
        </div>
      </section>

      <section className="border-t border-border bg-muted/30">
        <div className="mx-auto max-w-3xl px-5 py-16">
          <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
          <dl className="mt-8 space-y-7">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-semibold text-foreground">{f.q}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>
    </>
  );
}
